import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronRight, ChevronLeft, Sparkles, Send, CheckCircle2 } from 'lucide-react';
import StarRating from '../components/testimonials/StarRating';
import VideoRecorder from '../components/testimonials/VideoRecorder';
import VoiceButton from '../components/ui/VoiceButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTestimonial() {
    const { slug, token } = useParams();
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [answers, setAnswers] = useState({});
    const [videoBlob, setVideoBlob] = useState(null);
    const [improvingScript, setImprovingScript] = useState(false);
    const [teleprompterText, setTeleprompterText] = useState('');
    const [error, setError] = useState(null);

    const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';

    /* Load request config */
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/testimonials/public/${slug}/${token}`);
                setConfig(res.data);
                // Build initial teleprompter from previous text answers
                buildTeleprompter(res.data, {});
            } catch (err) {
                setError(err.response?.status === 404 ? 'This testimonial link is invalid or has expired.' : 'Failed to load testimonial form.');
            } finally { setLoading(false); }
        })();
    }, [slug, token]);

    /* Build teleprompter from text answers */
    const buildTeleprompter = useCallback((cfg, ans) => {
        if (!cfg?.pages) return;
        const textAnswers = [];
        cfg.pages.forEach(p => p.fields.forEach(f => {
            if ((f.field_type === 'text' || f.field_type === 'textarea') && ans[f.field_id]) {
                textAnswers.push(ans[f.field_id]);
            }
        }));
        if (textAnswers.length) setTeleprompterText(textAnswers.join(' '));
    }, []);

    const updateAnswer = (fieldId, value) => {
        const next = { ...answers, [fieldId]: value };
        setAnswers(next);
        buildTeleprompter(config, next);
    };

    /* AI Improve script */
    const improveScript = async () => {
        if (!teleprompterText.trim()) { toast.error('Write some text first for the AI to improve'); return; }
        setImprovingScript(true);
        try {
            const res = await axios.post(`${API}/testimonials/ai/improve-script`, {
                text: teleprompterText, token,
            });
            setTeleprompterText(res.data.improved_text);
            toast.success('Script improved!');
        } catch { toast.error('Failed to improve script'); }
        finally { setImprovingScript(false); }
    };

    /* Upload video and submit */
    const handleSubmit = async () => {
        // Validate required fields
        if (!config?.pages) return;
        for (const page of config.pages) {
            for (const field of page.fields) {
                if (field.required && !answers[field.field_id] && field.field_type !== 'video_recorder') {
                    toast.error(`"${field.label}" is required`);
                    // Navigate to the page with the missing field
                    const pageIdx = config.pages.indexOf(page);
                    setCurrentPage(pageIdx);
                    return;
                }
            }
        }

        setSubmitting(true);
        try {
            let videoS3Key = null;
            // Upload video if we have one
            if (videoBlob) {
                const uploadRes = await axios.post(`${API}/testimonials/upload-url`, {
                    token, content_type: videoBlob.type || 'video/webm',
                });
                const { upload_url, s3_key } = uploadRes.data;
                await axios.put(upload_url, videoBlob, {
                    headers: { 'Content-Type': videoBlob.type || 'video/webm' },
                });
                videoS3Key = s3_key;
            }

            await axios.post(`${API}/testimonials/public/${slug}/${token}`, {
                answers,
                video_s3_key: videoS3Key,
            });

            setSubmitted(true);
            // Post message for embed resize
            if (isEmbed) {
                window.parent.postMessage({ type: 'testimonial-submitted' }, '*');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit testimonial');
        } finally { setSubmitting(false); }
    };

    /* Render states */
    if (loading) return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
            <div className="text-center max-w-sm">
                <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-stone-400" />
                </div>
                <h1 className="text-lg font-semibold text-stone-800 mb-2">Oops</h1>
                <p className="text-sm text-stone-500">{error}</p>
            </div>
        </div>
    );

    if (submitted) {
        const confirmation = config?.settings?.confirmation_message || 'Thank you for your testimonial!';
        const redirect = config?.settings?.redirect_url;
        if (redirect) setTimeout(() => { window.location.href = redirect; }, 3000);
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
                <div className="text-center max-w-sm">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h1 className="text-lg font-semibold text-stone-800 mb-2">Submitted!</h1>
                    <p className="text-sm text-stone-500">{confirmation}</p>
                    {redirect && <p className="text-xs text-stone-400 mt-3">Redirecting...</p>}
                </div>
            </div>
        );
    }

    const pages = config?.pages || [];
    const page = pages[currentPage];
    const brandColor = config?.settings?.branding?.brand_color || '#1c1917';
    const logoUrl = config?.settings?.branding?.logo_url;
    const totalPages = pages.length;
    const isLast = currentPage === totalPages - 1;

    return (
        <div className="min-h-screen bg-stone-50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-stone-200 px-4 py-3">
                <div className="max-w-lg mx-auto flex items-center justify-between">
                    {logoUrl ? (
                        <img src={logoUrl} alt="" className="h-8 object-contain" />
                    ) : (
                        <div className="text-sm font-semibold" style={{ color: brandColor }}>{config?.name}</div>
                    )}
                    {totalPages > 1 && (
                        <div className="text-xs text-stone-400">
                            Step {currentPage + 1} of {totalPages}
                        </div>
                    )}
                </div>
            </div>

            {/* Progress dots */}
            {totalPages > 1 && (
                <div className="flex justify-center gap-2 py-4">
                    {pages.map((_, i) => (
                        <div key={i} className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i === currentPage ? 'scale-110' : 'opacity-50'
                        }`} style={{ backgroundColor: i <= currentPage ? brandColor : '#d6d3d1' }} />
                    ))}
                </div>
            )}

            {/* Page content */}
            <div className="flex-1 px-4 pb-6">
                <div className="max-w-lg mx-auto">
                    {page && (
                        <div>
                            <h2 className="text-xl font-semibold text-stone-900 mb-1">{page.title}</h2>
                            {page.description && <p className="text-sm text-stone-500 mb-6">{page.description}</p>}

                            <div className="space-y-5">
                                {page.fields.map(field => (
                                    <FieldRenderer key={field.field_id} field={field}
                                        value={answers[field.field_id]}
                                        onChange={v => updateAnswer(field.field_id, v)}
                                        onVideoComplete={setVideoBlob}
                                        teleprompterText={teleprompterText}
                                        onImproveScript={improveScript}
                                        improvingScript={improvingScript}
                                        brandColor={brandColor} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="sticky bottom-0 bg-white border-t border-stone-200 px-4 py-3">
                <div className="max-w-lg mx-auto flex items-center justify-between">
                    <button onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        className="flex items-center gap-1 px-4 py-2 text-sm text-stone-600 hover:text-stone-800 disabled:opacity-30 disabled:cursor-not-allowed">
                        <ChevronLeft className="w-4 h-4" /> Back
                    </button>
                    {isLast ? (
                        <button onClick={handleSubmit} disabled={submitting}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                            style={{ backgroundColor: brandColor }}>
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            Submit
                        </button>
                    ) : (
                        <button onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                            className="flex items-center gap-1 px-5 py-2.5 rounded-lg text-white text-sm font-medium transition-colors hover:opacity-90"
                            style={{ backgroundColor: brandColor }}>
                            Next <ChevronRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Powered by */}
            {config?.settings?.branding?.show_branding !== false && (
                <div className="text-center py-2 text-xs text-stone-400">
                    Powered by <span className="font-medium">NurtureCRM</span>
                </div>
            )}
        </div>
    );
}

/* ---- Field Renderer ---- */
function FieldRenderer({ field, value, onChange, onVideoComplete, teleprompterText, onImproveScript, improvingScript, brandColor }) {
    const INPUT = "w-full px-4 py-3 text-base border border-stone-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 transition-shadow";

    switch (field.field_type) {
        case 'text':
            return (
                <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                        <input value={value || ''} onChange={e => onChange(e.target.value)}
                            className={INPUT} placeholder={field.placeholder} />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <VoiceButton onTranscript={t => onChange((value || '') + ' ' + t)} />
                        </div>
                    </div>
                </div>
            );

        case 'textarea':
            return (
                <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                        <textarea value={value || ''} onChange={e => onChange(e.target.value)}
                            className={`${INPUT} min-h-[120px]`} rows={4} placeholder={field.placeholder} />
                        <div className="absolute right-2 bottom-2">
                            <VoiceButton onTranscript={t => onChange((value || '') + ' ' + t)} />
                        </div>
                    </div>
                </div>
            );

        case 'rating':
            return (
                <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <StarRating value={value || 0} onChange={onChange} maxStars={field.options?.max_stars || 5} size="lg" />
                </div>
            );

        case 'video_recorder':
            return (
                <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {/* AI Improve button */}
                    {teleprompterText && field.options?.show_teleprompter !== false && (
                        <button onClick={onImproveScript} disabled={improvingScript}
                            className="flex items-center gap-1.5 px-3 py-1.5 mb-3 text-xs font-medium rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-600 transition-colors">
                            {improvingScript ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            AI Improve Script
                        </button>
                    )}
                    <VideoRecorder
                        maxSeconds={field.options?.max_seconds || 30}
                        onRecordingComplete={onVideoComplete}
                        teleprompterText={field.options?.show_teleprompter !== false ? teleprompterText : ''} />
                </div>
            );

        default:
            return null;
    }
}
